/**
 * Custom hook for managing job posting form state and logic
 */

import { useState, useCallback, useRef } from 'react';
import employerService from '../../utils/employerService';
import { getJobByIdOrSlug } from '../../utils/jobLookupService';
import { generateJobSlug } from '../../utils/slugGenerator';
import formService from '../../services/formService';
import {
  validateJobStep,
  validateAllSteps,
  prepareJobPayload,
  formatDateForInput,
  sanitizeResponsibilities
} from './utils';
import { INITIAL_JOB_DATA } from './constants';

export const useJobPostingForm = (jobId, user, userProfile, onSuccess) => {
  // State
  const [jobData, setJobData] = useState(() =>
    INITIAL_JOB_DATA(userProfile?.company_name)
  );
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Use ref to track if we've loaded data to avoid double-loading
  const hasLoadedData = useRef(false);

  /**
   * Load existing job data for editing
   * Supports both UUID and public slug lookups
   */
  const loadJobData = useCallback(
    async (id) => {
      if (!id || hasLoadedData.current) return;
      
      try {
        setLoading(true);
        hasLoadedData.current = true;
        
        // Use flexible lookup by UUID or slug
        const { data: job, error } = await getJobByIdOrSlug(id);
        
        if (error || !job) {
          setErrors({ general: 'Failed to load job data' });
          return;
        }

        // Verify ownership before allowing edit
        if (job.employer_id !== user?.id) {
          setErrors({ general: 'You do not have permission to edit this job' });
          return;
        }

        setJobData({
          id: job.id,
          public_slug: job.public_slug,
          title: job.title || '',
          description: job.description || '',
          requirements: Array.isArray(job.requirements)
            ? job.requirements.join('\n')
            : job.requirements || '',
          responsibilities: sanitizeResponsibilities(job.responsibilities || ''),
          skills_required: Array.isArray(job.skills_required)
            ? job.skills_required
            : Array.isArray(job.required_skills)
            ? job.required_skills
            : [],
          experience_level: job.experience_level || 'intermediate',
          employment_type: job.job_type || 'full-time',
          location: job.location || '',
          remote_work: job.is_remote ? 'remote' : job.location ? 'onsite' : 'hybrid',
          salary_min: job.salary_min || job.budget_min || '',
          salary_max: job.salary_max || job.budget_max || '',
          currency: job.currency || 'USD',
          benefits: Array.isArray(job.benefits) ? job.benefits : [],
          application_deadline: formatDateForInput(job.application_deadline),
          start_date: formatDateForInput(job.start_date),
          company_name: job.company_name || userProfile?.company_name || '',
          company_description: job.company_description || '',
          status: job.status || 'draft'
        });
        setIsEditMode(true);
      } catch (error) {
        console.error('Error loading job:', error);
        setErrors({ general: 'Failed to load job data' });
      } finally {
        setLoading(false);
      }
    },
    [user?.id, userProfile?.company_name]
  );

  /**
   * Check if a job with the same title already exists
   */
  const checkDuplicateJob = useCallback(
    async (title) => {
      if (!title?.trim() || !user?.id) return false;

      try {
        const result = await employerService.getEmployerJobs(user.id);

        if (!result.success) {
          console.error('Error checking for duplicate jobs:', result.error);
          return false;
        }

        const duplicateJob = result.data.find(
          job =>
            job.title.toLowerCase() === title.toLowerCase() &&
            (!isEditMode || job.id !== jobId)
        );

        return !!duplicateJob;
      } catch (error) {
        console.error('Error in checkDuplicateJob:', error);
        return false;
      }
    },
    [user?.id, isEditMode, jobId]
  );

  /**
   * Validate current step
   */
  const validateStep = useCallback(
    (step) => validateJobStep(step, jobData),
    [jobData]
  );

  /**
   * Move to next step with validation
   */
  const handleNext = useCallback(() => {
    const stepErrors = validateStep(currentStep);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    setCurrentStep(prev => Math.min(prev + 1, 4));
  }, [currentStep, validateStep]);

  /**
   * Move to previous step
   */
  const handlePrevious = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  }, []);

  /**
   * Update job data field
   */
  const updateJobData = useCallback((updates) => {
    setJobData(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Toggle benefit selection
   */
  const toggleBenefit = useCallback((benefit) => {
    setJobData(prev => ({
      ...prev,
      benefits: prev.benefits.includes(benefit)
        ? prev.benefits.filter(b => b !== benefit)
        : [...prev.benefits, benefit]
    }));
  }, []);

  /**
   * Save job as draft
   */
  const saveDraft = useCallback(
    async (status = 'draft') => {
      setLoading(true);
      setErrors({});

      try {
        // Validate current step
        const stepErrors = validateStep(currentStep);
        if (Object.keys(stepErrors).length > 0) {
          setErrors(stepErrors);
          return { success: false };
        }

        // Check for duplicate job title if creating a new job
        if (!isEditMode) {
          const isDuplicate = await checkDuplicateJob(jobData.title);
          if (isDuplicate) {
            setErrors({
              title: 'A job with this title already exists. Please use a different title.'
            });
            return { success: false };
          }
        }

        const payload = prepareJobPayload(jobData, user?.id, userProfile?.company_name);
        payload.status = status;

        // Generate public_slug for new jobs
        if (!isEditMode && !payload.public_slug) {
          payload.public_slug = generateJobSlug(jobData.title);
        }

        const result = isEditMode
          ? await employerService.updateJob(jobData.id, payload)
          : await employerService.createJob(payload);

        if (!result.success) {
          throw new Error(result.error);
        }

        const newJobId = result.data?.id;
        const publicSlug = result.data?.public_slug || payload.public_slug;
        const finalJobId = newJobId || jobId;

        // Attach form to job if form was selected
        let formAttached = false;
        let formError = null;
        if (jobData.selected_form_id && finalJobId) {
          try {
            await formService.attachFormToJob(jobData.selected_form_id, finalJobId);
            formAttached = true;
          } catch (error) {
            console.error('Failed to attach form to job:', error);
            formError = error?.message || 'Failed to attach form to job';
            // Don't fail the entire save operation if form attachment fails
          }
        }

        const message = isEditMode
          ? 'Job updated successfully!'
          : 'Job draft saved successfully!';

        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(''), 3000);

        return {
          success: true,
          jobId: finalJobId,
          publicSlug: publicSlug || jobData.public_slug,
          isNewJob: !isEditMode && !!newJobId,
          formAttached,
          formError
        };
      } catch (err) {
        console.error('Save draft error:', err);
        setErrors({ general: 'Failed to save draft. Please try again.' });
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [currentStep, validateStep, isEditMode, checkDuplicateJob, jobData, user?.id, userProfile?.company_name, jobId]
  );

  /**
   * Publish job (full validation)
   */
  const publishJob = useCallback(
    async (canPerformAction, recordAction) => {
      // Check subscription limits before proceeding
      if (!isEditMode) {
        const jobLimits = await canPerformAction('active_jobs');
        if (!jobLimits.allowed) {
          setErrors({
            general: `You've reached your job posting limit. Upgrade your plan to post more jobs. Remaining: ${jobLimits.remaining}`
          });
          return { success: false };
        }
      }

      // Validate all steps
      const allErrors = validateAllSteps(jobData);
      if (Object.keys(allErrors).length > 0) {
        setErrors(allErrors);
        return { success: false };
      }

      setLoading(true);

      try {
        // Check for duplicate job title if creating a new job
        if (!isEditMode) {
          const isDuplicate = await checkDuplicateJob(jobData.title);
          if (isDuplicate) {
            setErrors({
              title: 'A job with this title already exists. Please use a different title.'
            });
            return { success: false };
          }
        }

        const payload = prepareJobPayload(jobData, user?.id, userProfile?.company_name);
        
        // Generate public_slug for new jobs
        if (!isEditMode && !payload.public_slug) {
          payload.public_slug = generateJobSlug(jobData.title);
        }
        
        // For free tier users, set status to draft and moderation_status to pending for admin review
        // For paid tiers, set status to published (auto-approved)
        // NOTE: subscription_tier may be null/undefined for free users, so we check for paid tiers explicitly
        const isPaidTier = ['pro', 'premium', 'agency'].includes(userProfile?.subscription_tier);
        if (!isPaidTier && !isEditMode) {
          payload.status = 'draft';
          payload.moderation_status = 'pending';
          console.log('[publishJob] Free/unsubscribed user - setting status to draft with moderation_status pending');
        } else {
          payload.status = 'published';
        }

        const result = isEditMode
          ? await employerService.updateJob(jobData.id, payload)
          : await employerService.createJob(payload);

        if (!result.success) {
          throw new Error(result.error);
        }

        // Record job posting action for subscription usage
        if (!isEditMode && recordAction) {
          await recordAction('job');
        }

        const finalJobId = result.data?.id || jobId;

        // Attach form to job if form was selected
        let formAttached = false;
        let formError = null;
        if (jobData.selected_form_id && finalJobId) {
          try {
            await formService.attachFormToJob(jobData.selected_form_id, finalJobId);
            formAttached = true;
          } catch (error) {
            console.error('Failed to attach form to job:', error);
            formError = error?.message || 'Failed to attach form to job';
            // Don't fail the entire publish operation if form attachment fails
          }
        }

        const publicSlug = result.data?.public_slug || payload.public_slug;
        const message = isEditMode
          ? 'Job updated and published successfully!'
          : !isPaidTier
          ? 'Job submitted for review. Our admin team will review and approve it shortly.'
          : 'Job posted successfully!';

        setSuccessMessage(message);

        return {
          success: true,
          jobId: finalJobId,
          publicSlug: publicSlug || jobData.public_slug,
          isNewJob: !isEditMode && !!result.data?.id,
          formAttached,
          formError
        };
      } catch (err) {
        console.error('Publish job error:', err);
        setErrors({ general: 'Failed to publish job. Please try again.' });
        return { success: false };
      } finally {
        setLoading(false);
      }
    },
    [isEditMode, validateAllSteps, checkDuplicateJob, jobData, user?.id, userProfile?.company_name, jobId]
  );

  /**
   * Apply AI-generated content to job form
   * Integrates generated job posting content into the form state
   */
  const setAIGenerated = useCallback((generatedContent) => {
    try {
      // Validate that we have the required fields from AI generation
      if (!generatedContent || typeof generatedContent !== 'object') {
        console.error('Invalid AI generated content:', generatedContent);
        setErrors({ general: 'Failed to apply AI-generated content. Invalid format.' });
        return false;
      }

      // Extract AI content with fallbacks
      // Important: Use 'skills_required' which matches the database column name
      const aiData = {
        title: generatedContent.title || '',
        description: generatedContent.description || '',
        responsibilities: Array.isArray(generatedContent.responsibilities)
          ? generatedContent.responsibilities.join('\n')
          : typeof generatedContent.responsibilities === 'string'
          ? generatedContent.responsibilities.split(',').map(item => item.trim()).join('\n')
          : '',
        requirements: Array.isArray(generatedContent.qualifications)
          ? generatedContent.qualifications.join('\n')
          : generatedContent.qualifications || '',
        skills_required: Array.isArray(generatedContent.skills)
          ? generatedContent.skills
          : [],
        benefits: Array.isArray(generatedContent.suggestedBenefits)
          ? generatedContent.suggestedBenefits
          : [],
        salary_min: generatedContent.suggestedSalaryRange?.min || jobData.salary_min,
        salary_max: generatedContent.suggestedSalaryRange?.max || jobData.salary_max,
        currency: generatedContent.suggestedSalaryRange?.currency || jobData.currency,
        location: generatedContent.location || jobData.location,
        remote_work: generatedContent.isRemote ? 'remote' : jobData.remote_work
      };

      // Update job data with AI content
      setJobData(prev => ({
        ...prev,
        ...aiData,
        // Preserve user's existing selections
        employment_type: prev.employment_type,
        experience_level: prev.experience_level,
        company_name: prev.company_name,
        company_description: prev.company_description,
        application_deadline: prev.application_deadline,
        start_date: prev.start_date
      }));

      // Move to step 1 (Job Details) to let user review and complete the normal workflow
      setCurrentStep(1);
      setErrors({});
      
      return true;
    } catch (error) {
      console.error('Error applying AI-generated content:', error);
      setErrors({ general: 'Failed to apply AI-generated content. Please try again.' });
      return false;
    }
  }, [jobData.salary_min, jobData.salary_max, jobData.currency]);

  return {
    // State
    jobData,
    loading,
    errors,
    successMessage,
    currentStep,
    isEditMode,
    
    // Actions
    loadJobData,
    handleNext,
    handlePrevious,
    updateJobData,
    toggleBenefit,
    saveDraft,
    publishJob,
    validateStep,
    setErrors,
    setSuccessMessage,
    setCurrentStep,
    checkDuplicateJob,
    setAIGenerated
  };
};
